import React, { useState, useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import withRouter from "components/Common/withRouter";
import Select, { components } from "react-select";

import {
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Container,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";

import {
  fetchAIScheduleMatrixByGroup,
  fetchAIScheduleMatrixDays,
  fetchAIScheduleMatrixWeekDays,
  submitAIScheduleMatrixBulk,
} from "model/AIScheduleMatrixModel";
import { getCommonScheduleMatrixGroupOptions } from "model/ClientRegisterModel";
import { submitAISmsTemplateFilterData } from "model/AISmsTemplateModel";
import { usePermissionContext } from "libs/context/PermissionContext";
import {
  AI_SCHEDULE_ACTION_TYPE,
  ACTION_TYPE_OPTIONS,
  SCHEDULE_MODE,
  SCHEDULE_PERIOD_TYPE,
  SCHEDULE_PERIOD_OPTIONS,
  DAY_NO_OPTIONS,
  WEEK_DAY_NO_OPTIONS,
  clampScheduleTimeValue,
  normalizeTimeValue,
  selectMenuStyles,
} from "pages/AllCommonModal/AIScheduleMatrixAddEditModal/ai-schedule-matrix-add-edit-modal";
import ScheduleMatrixTimeInput from "pages/AllCommonModal/AIScheduleMatrixAddEditModal/schedule-matrix-time-input";
import Spinners from "components/Common/Spinner";
import AIScheduleMatrixGroupAddEditModal from "pages/AllCommonModal/AIScheduleMatrixGroupAddEditModal/ai-schedule-matrix-group-add-edit-modal";
import { toast } from "react-toastify";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import "./ai-schedule-matrix.scss";

let scheduleFieldKeySeq = 0;
const nextScheduleFieldKey = () =>
  `schedule-field-${scheduleFieldKeySeq++}`;

const selectZIndexStyles = selectMenuStyles;

const SMS_TEMPLATE_REQUIRED_MSG =
  "SMS template is required when basis is SMS";

const RequiredMark = () => (
  <span className="schedule-matrix-required-mark text-danger" aria-hidden="true">
    *
  </span>
);

const normalizeCommonList = (response) =>
  response?.data?.records || response?.data?.data || response?.data || response || [];

const mapScheduleMatrixGroupOptions = (response) =>
  (Array.isArray(normalizeCommonList(response)) ? normalizeCommonList(response) : [])
    .map((item) => {
      const value = String(item?.id ?? item?.Id ?? item?.value ?? "").trim();
      const label = String(item?.display ?? item?.name ?? item?.label ?? value).trim();
      if (!value) return null;
      return { value, label: label || value };
    })
    .filter(Boolean);

const mapScheduleWhenSelectOptions = (response, whenKey) =>
  (Array.isArray(normalizeCommonList(response)) ? normalizeCommonList(response) : [])
    .map((item) => {
      const whenValue = item?.[whenKey];
      const value =
        whenValue === 0 || whenValue
          ? whenValue
          : item?.value ?? item?.number ?? item?.id ?? item?.Id;
      if (value === null || value === undefined || value === "") return null;
      const label = String(
        item?.label ??
          item?.display ??
          item?.name ??
          item?.Name ??
          item?.title ??
          value
      ).trim();
      return { value: String(value), label: label || String(value) };
    })
    .filter(Boolean);

const mapDayNoSelectOptions = (response) =>
  mapScheduleWhenSelectOptions(response, "dayNo");

const mapWeekDayNoSelectOptions = (response) =>
  mapScheduleWhenSelectOptions(response, "weekDayNo");

const toNumberOrZero = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const toOptionalNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const timeValueToSeconds = (timeStr) => {
  const normalized = normalizeTimeValue(timeStr);
  if (!normalized) return null;
  const parts = normalized.split(":").map((part) => Number(part));
  if (parts.length < 2 || parts.some((n) => !Number.isFinite(n))) return null;
  const [hours, minutes, seconds = 0] = parts;
  return hours * 3600 + minutes * 60 + seconds;
};

const formatTimeLabel = (timeStr) => {
  const normalized = normalizeTimeValue(timeStr);
  if (!normalized) return "";
  const [hours, minutes] = normalized.split(":");
  return `${hours}:${minutes}`;
};

const getScheduleWhenNumber = (field) => {
  if (field?.schedulePeriod === SCHEDULE_PERIOD_TYPE.WEEK) {
    return toOptionalNumber(field?.weekDayNo);
  }
  return toOptionalNumber(field?.dayNo);
};

const getScheduleWhenLabel = (field) =>
  field?.schedulePeriod === SCHEDULE_PERIOD_TYPE.WEEK ? "Week day" : "Day";

/** Mon=1 … Sun=7 for ordering; some APIs use 0 for Sunday. */
const normalizeWeekDayOrderNumber = (value) => {
  const n = toOptionalNumber(value);
  if (n === null) return null;
  if (n === 0) return 7;
  return n;
};

const getScheduleWhenOrderNumber = (field) => {
  if (field?.schedulePeriod === SCHEDULE_PERIOD_TYPE.WEEK) {
    return normalizeWeekDayOrderNumber(field?.weekDayNo);
  }
  return getScheduleWhenNumber(field);
};

const formatWhenOrderReference = (field, orderNumber) => {
  if (field?.schedulePeriod === SCHEDULE_PERIOD_TYPE.WEEK) {
    const match = WEEK_DAY_NO_OPTIONS.find(
      (opt) => normalizeWeekDayOrderNumber(opt.value) === orderNumber
    );
    return match?.label || String(orderNumber);
  }
  return String(orderNumber);
};

const isScheduleRowOrdered = (field) => getScheduleWhenNumber(field) !== null;

const isSameSchedulePeriod = (a, b) =>
  Boolean(a?.schedulePeriod) && a.schedulePeriod === b?.schedulePeriod;

const findPreviousSamePeriodField = (fields, fieldIndex, isRowActive) => {
  const current = fields?.[fieldIndex];
  if (!current) return null;
  for (let i = fieldIndex - 1; i >= 0; i -= 1) {
    const prev = fields[i];
    if (typeof isRowActive === "function" && !isRowActive(prev)) continue;
    if (!isScheduleRowOrdered(prev)) continue;
    if (isSameSchedulePeriod(prev, current)) return prev;
  }
  return null;
};

const compareSameDayTimeViolation = (prevField, nextField) => {
  const prevSeconds = timeValueToSeconds(prevField?.time);
  const nextSeconds = timeValueToSeconds(nextField?.time);
  if (prevSeconds === null || nextSeconds === null) {
    return null;
  }
  if (nextSeconds < prevSeconds) {
    const whenLabel = getScheduleWhenLabel(nextField);
    const prevTimeLabel = formatTimeLabel(prevField.time) || prevField.time;
    return `On the same ${whenLabel.toLowerCase()}, time cannot be earlier than the previous row (${prevTimeLabel}).`;
  }
  return null;
};

const getScheduleOrderIssue = (prevField, nextField) => {
  if (!isSameSchedulePeriod(prevField, nextField)) return null;

  const whenLabel = getScheduleWhenLabel(nextField);
  const prevWhen = getScheduleWhenOrderNumber(prevField);
  const nextWhen = getScheduleWhenOrderNumber(nextField);
  if (prevWhen === null || nextWhen === null) return null;

  if (nextWhen < prevWhen) {
    const prevRef = formatWhenOrderReference(prevField, prevWhen);
    const highlightKeys =
      nextField.schedulePeriod === SCHEDULE_PERIOD_TYPE.WEEK
        ? ["weekDayNo"]
        : ["dayNo"];
    return {
      message: `${whenLabel} cannot be lower than the previous row (${prevRef}). Choose ${prevRef} or later.`,
      highlightKeys,
    };
  }
  if (nextWhen !== prevWhen) {
    return null;
  }
  const timeMessage = compareSameDayTimeViolation(prevField, nextField);
  if (!timeMessage) return null;
  return { message: timeMessage, highlightKeys: ["time"] };
};

const markScheduleFieldError = (fieldErrors, sectionIndex, fieldIndex, key) => {
  if (!fieldErrors[sectionIndex]) {
    fieldErrors[sectionIndex] = {};
  }
  if (!fieldErrors[sectionIndex][fieldIndex]) {
    fieldErrors[sectionIndex][fieldIndex] = {};
  }
  fieldErrors[sectionIndex][fieldIndex][key] = true;
};

const collectScheduleOrderValidation = (
  groupSections,
  isRowActive,
  { onlySectionIndex } = {}
) => {
  const rowActive =
    typeof isRowActive === "function"
      ? isRowActive
      : () => true;
  const fieldErrors = {};
  let message = null;
  let firstSectionIndex = null;

  for (let sectionIndex = 0; sectionIndex < (groupSections || []).length; sectionIndex += 1) {
    if (
      onlySectionIndex !== undefined &&
      onlySectionIndex !== null &&
      sectionIndex !== onlySectionIndex
    ) {
      continue;
    }
    const section = groupSections[sectionIndex];
    const fields = section.fields || [];
    const stageName =
      String(section.stageName || "").trim() || `Stage ${sectionIndex + 1}`;

    for (let fieldIndex = 0; fieldIndex < fields.length; fieldIndex += 1) {
      const curr = fields[fieldIndex];
      if (!rowActive(curr) || !isScheduleRowOrdered(curr)) continue;
      const prev = findPreviousSamePeriodField(fields, fieldIndex, rowActive);
      if (!prev) continue;
      const issue = getScheduleOrderIssue(prev, curr);
      if (!issue) continue;
      issue.highlightKeys.forEach((key) =>
        markScheduleFieldError(fieldErrors, sectionIndex, fieldIndex, key)
      );
      if (!message) {
        message = `${stageName}: ${issue.message}`;
        firstSectionIndex = sectionIndex;
      }
    }
  }

  if (!message) {
    return { fieldErrors: {}, message: null, firstSectionIndex: null };
  }
  return { fieldErrors, message, firstSectionIndex };
};

const runGroupSectionsValidation = (
  groupSections,
  isRowActive,
  { onlySectionIndex } = {}
) => {
  const fieldErrors = {};
  let message = null;
  let firstSectionIndex = null;

  (groupSections || []).forEach((section, sectionIndex) => {
    if (
      onlySectionIndex !== undefined &&
      onlySectionIndex !== null &&
      sectionIndex !== onlySectionIndex
    ) {
      return;
    }
    const stageName =
      String(section.stageName || "").trim() || `Stage ${sectionIndex + 1}`;
    section.fields.forEach((field, fieldIndex) => {
      const rowLabel = `${stageName}, row ${fieldIndex + 1}`;

      if (!isRowActive(field)) {
        markScheduleFieldError(
          fieldErrors,
          sectionIndex,
          fieldIndex,
          "actionTypeId"
        );
        if (!message) {
          message = `${rowLabel}: Select Call or SMS to complete this schedule row, or remove it before saving`;
          firstSectionIndex = sectionIndex;
        }
        return;
      }

      if (
        field.schedulePeriod === SCHEDULE_PERIOD_TYPE.DAY &&
        field.dayNo === ""
      ) {
        markScheduleFieldError(fieldErrors, sectionIndex, fieldIndex, "dayNo");
        if (!message) {
          message = `${rowLabel}: Day is required`;
          firstSectionIndex = sectionIndex;
        }
      }
      if (
        field.schedulePeriod === SCHEDULE_PERIOD_TYPE.WEEK &&
        field.weekDayNo === ""
      ) {
        markScheduleFieldError(fieldErrors, sectionIndex, fieldIndex, "weekDayNo");
        if (!message) {
          message = `${rowLabel}: Week day is required`;
          firstSectionIndex = sectionIndex;
        }
      }
      if (
        Number(field.actionTypeId) === AI_SCHEDULE_ACTION_TYPE.SMS &&
        !String(field.aiSmsTemplateId ?? "").trim()
      ) {
        markScheduleFieldError(
          fieldErrors,
          sectionIndex,
          fieldIndex,
          "aiSmsTemplateId"
        );
        if (!message) {
          message = `${rowLabel}: ${SMS_TEMPLATE_REQUIRED_MSG}`;
          firstSectionIndex = sectionIndex;
        }
      }
    });
  });

  if (message) {
    return { ok: false, message, fieldErrors, firstSectionIndex };
  }

  const orderValidation = collectScheduleOrderValidation(groupSections, isRowActive, {
    onlySectionIndex,
  });
  if (orderValidation.message) {
    return {
      ok: false,
      message: orderValidation.message,
      fieldErrors: {
        ...fieldErrors,
        ...orderValidation.fieldErrors,
      },
      firstSectionIndex: orderValidation.firstSectionIndex,
    };
  }

  return { ok: true, message: null, fieldErrors: {}, firstSectionIndex: null };
};

const reindexSectionFields = (fields) =>
  (Array.isArray(fields) ? fields : []).map((field, idx) => ({
    ...field,
    rowIndex: idx,
  }));

const createEmptyField = () => ({
  id: null,
  localKey: nextScheduleFieldKey(),
  rowIndex: null,
  actionTypeId: "",
  schedulePeriod: SCHEDULE_PERIOD_TYPE.DAY,
  dayNo: "",
  weekDayNo: "",
  time: "",
  aiSmsTemplateId: "",
  aiSmsTemplateName: "",
});

const inferSchedulePeriod = (row) => {
  if (Number(row?.scheduleMode) === SCHEDULE_MODE.WEEK) {
    return SCHEDULE_PERIOD_TYPE.WEEK;
  }
  return SCHEDULE_PERIOD_TYPE.DAY;
};

const rowToField = (row) => {
  const schedulePeriod = inferSchedulePeriod(row);
  return {
    id: row?.id ?? null,
    localKey: String(row?.id ?? `stage-row-${row?.rowIndex ?? 0}`),
    rowIndex: row?.rowIndex ?? null,
    actionTypeId:
      row?.actionTypeId === 0 || row?.actionTypeId
        ? String(row.actionTypeId)
        : "",
    schedulePeriod,
    dayNo:
      row?.dayNo === 0 || row?.dayNo ? String(row.dayNo) : "",
    weekDayNo:
      row?.weekDayNo === 0 || row?.weekDayNo ? String(row.weekDayNo) : "",
    time: normalizeTimeValue(row?.time),
    aiSmsTemplateId: row?.aiSmsTemplateId ? String(row.aiSmsTemplateId) : "",
    aiSmsTemplateName: String(
      row?.aiSmsTemplateName ?? row?.aiSmsTemplateNote ?? row?.note ?? ""
    ),
  };
};

const extractStagesFromByGroupResponse = (response) => {
  const raw = response?.data;
  return Array.isArray(raw) ? raw : [];
};

const stagesToSections = (stages) =>
  (Array.isArray(stages) ? stages : [])
    .map((stage) => ({
      localKey: String(stage?.stageId ?? ""),
      stageId: String(stage?.stageId ?? ""),
      stageName: String(stage?.stageName ?? "").trim(),
      fields: reindexSectionFields(
        (Array.isArray(stage?.scheduleMatrix) ? stage.scheduleMatrix : [])
          .map(rowToField)
          .sort(
            (a, b) =>
              toNumberOrZero(a.rowIndex) - toNumberOrZero(b.rowIndex)
          )
      ),
    }))
    .map((section) => ({
      ...section,
      fields: section.fields.length ? section.fields : [],
    }))
    .sort((a, b) => toNumberOrZero(a.stageId) - toNumberOrZero(b.stageId));

/** Copy submit: same row values as source API, target groupId, id null. */
const buildCopyRowsFromSourceStages = (stages, targetGroupId, buildPayloadFn) => {
  if (!buildPayloadFn) return [];
  const resolvedTargetGroupId = String(targetGroupId ?? "").trim();
  if (!resolvedTargetGroupId) return [];

  return (Array.isArray(stages) ? stages : []).flatMap((stage) => {
    const list = Array.isArray(stage?.scheduleMatrix) ? [...stage.scheduleMatrix] : [];
    list.sort((a, b) => toNumberOrZero(a.rowIndex) - toNumberOrZero(b.rowIndex));
    const section = { stageId: String(stage?.stageId ?? "") };
    return list.map((row, fieldIndex) => {
      const field = rowToField(row);
      const payloadRowIndex =
        row?.rowIndex === 0 || row?.rowIndex ? row.rowIndex : fieldIndex;
      return buildPayloadFn(
        resolvedTargetGroupId,
        section,
        field,
        payloadRowIndex,
        { asAdd: true }
      );
    });
  });
};

const cloneSectionsForTargetGroup = (groupSections) =>
  (Array.isArray(groupSections) ? groupSections : []).map((section) => {
    const activeFields = (section.fields || []).filter((field) => {
      const hasAction =
        field?.actionTypeId !== "" &&
        field?.actionTypeId !== null &&
        field?.actionTypeId !== undefined;
      return hasAction;
    });
    const copiedFields = (activeFields.length ? activeFields : []).map((field) => ({
      ...field,
      id: null,
      localKey: nextScheduleFieldKey(),
    }));
    return {
      ...section,
      fields: reindexSectionFields(copiedFields),
    };
  });

const mapSmsTemplateOptions = (list = []) =>
  (Array.isArray(list) ? list : [])
    .map((item) => {
      const value = item?.id ?? item?.Id;
      if (value == null || value === "") return null;
      const label =
        String(item?.note ?? "").trim() ||
        String(item?.name ?? item?.Name ?? "").trim() ||
        String(value);
      return { value, label };
    })
    .filter(Boolean);

const AIScheduleMatrix = () => {
  const { permissionData } = usePermissionContext() || {};

  const actionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.AIScheduleMatrixAddAccess),
      canEdit: Boolean(permissionData?.AIScheduleMatrixUpdateAccess),
      canDelete: Boolean(permissionData?.AIScheduleMatrixDeleteAccess),
      canSave: Boolean(
        permissionData?.AIScheduleMatrixAddAccess ||
          permissionData?.AIScheduleMatrixUpdateAccess
      ),
    }),
    [permissionData]
  );

  const groupActionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.AIScheduleMatrixGroupAddAccess),
      canEdit: Boolean(permissionData?.AIScheduleMatrixGroupUpdateAccess),
    }),
    [permissionData]
  );

  const [optionsLoading, setOptionsLoading] = useState(true);
  const [scheduleMatrixGroupOptions, setScheduleMatrixGroupOptions] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [openSection, setOpenSection] = useState(null);
  const [dayNoOptions, setDayNoOptions] = useState([]);
  const [weekDayNoOptions, setWeekDayNoOptions] = useState([]);
  const [smsTemplateOptions, setSmsTemplateOptions] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingSectionIndex, setSavingSectionIndex] = useState(null);
  const [matrixLoaded, setMatrixLoaded] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySourceGroupId, setCopySourceGroupId] = useState("");
  const [copying, setCopying] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupEditItemId, setGroupEditItemId] = useState(null);

  const isScheduleRowActive = (field) =>
    field?.actionTypeId !== "" &&
    field?.actionTypeId !== null &&
    field?.actionTypeId !== undefined;

  const showMatrixEditor =
    Boolean(selectedGroupId) && scheduleMatrixGroupOptions.length > 0;

  const showToolbarActions = showMatrixEditor && actionPermissions.canSave;

  const loadScheduleMatrixGroupOptions = useCallback(async () => {
    const response = await getCommonScheduleMatrixGroupOptions();
    const nextOptions = mapScheduleMatrixGroupOptions(response);
    setScheduleMatrixGroupOptions(nextOptions);
    return nextOptions;
  }, []);

  const openGroupCreateModal = useCallback(() => {
    setGroupEditItemId(null);
    setGroupModalOpen(true);
  }, []);

  const openGroupEditModal = useCallback((groupId) => {
    const resolvedId = String(groupId ?? "").trim();
    if (!resolvedId) return;
    setGroupEditItemId(resolvedId);
    setGroupModalOpen(true);
  }, []);

  const closeGroupModal = useCallback(() => {
    setGroupModalOpen(false);
    setGroupEditItemId(null);
  }, []);

  const handleGroupModalSuccess = useCallback(async () => {
    await loadScheduleMatrixGroupOptions();
  }, [loadScheduleMatrixGroupOptions]);

  const loadSmsTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const response = await submitAISmsTemplateFilterData({});
      const responseData = response?.data;
      const listData = Array.isArray(responseData)
        ? responseData
        : responseData?.records ?? [];
      setSmsTemplateOptions(mapSmsTemplateOptions(listData));
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const loadDayAndWeekOptions = useCallback(async () => {
    const [daysRes, weekDaysRes] = await Promise.all([
      fetchAIScheduleMatrixDays(),
      fetchAIScheduleMatrixWeekDays(),
    ]);
    const days = mapDayNoSelectOptions(daysRes);
    const weekDays = mapWeekDayNoSelectOptions(weekDaysRes);
    setDayNoOptions(days.length ? days : DAY_NO_OPTIONS);
    setWeekDayNoOptions(weekDays.length ? weekDays : WEEK_DAY_NO_OPTIONS);
  }, []);

  useEffect(() => {
    let active = true;
    const init = async () => {
      setOptionsLoading(true);
      try {
        await Promise.all([
          loadScheduleMatrixGroupOptions(),
          loadSmsTemplates(),
          loadDayAndWeekOptions(),
        ]);
      } finally {
        if (active) setOptionsLoading(false);
      }
    };
    init();
    return () => {
      active = false;
    };
  }, [loadScheduleMatrixGroupOptions, loadSmsTemplates, loadDayAndWeekOptions]);

  const loadMatrixForGroup = useCallback(async (groupId, { openSectionIndex } = {}) => {
    const resolvedGroupId = String(groupId ?? "").trim();
    if (!resolvedGroupId) {
      setSections([]);
      return;
    }

    setLoading(true);
    setMatrixLoaded(false);
    try {
      const response = await fetchAIScheduleMatrixByGroup(resolvedGroupId);
      const nextSections = stagesToSections(extractStagesFromByGroupResponse(response));
      setSections(nextSections);
      setFieldErrors({});
      if (
        openSectionIndex !== undefined &&
        openSectionIndex !== null &&
        nextSections.length > 0
      ) {
        const clampedIndex = Math.min(
          Math.max(0, openSectionIndex),
          nextSections.length - 1
        );
        setOpenSection(clampedIndex);
      } else {
        setOpenSection(nextSections.length > 0 ? 0 : null);
      }
      setMatrixLoaded(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleGroupChange = (selectedOption) => {
    const nextGroupId = selectedOption?.value ? String(selectedOption.value) : "";
    setSelectedGroupId(nextGroupId);
    setFieldErrors({});
    setMatrixLoaded(false);
    if (!nextGroupId) {
      setSections([]);
      setOpenSection(null);
      return;
    }
    setSections([]);
    setOpenSection(null);
    loadMatrixForGroup(nextGroupId);
  };

  const getSelectedGroupOption = () =>
    scheduleMatrixGroupOptions.find(
      (option) => String(option?.value) === String(selectedGroupId)
    ) || null;

  const copySourceGroupOptions = useMemo(
    () =>
      scheduleMatrixGroupOptions.filter(
        (option) => String(option?.value) !== String(selectedGroupId)
      ),
    [scheduleMatrixGroupOptions, selectedGroupId]
  );

  const getCopySourceGroupOption = () =>
    copySourceGroupOptions.find(
      (option) => String(option?.value) === String(copySourceGroupId)
    ) || null;

  const openCopyModal = () => {
    if (!selectedGroupId) {
      toast.error("Select a schedule matrix group first");
      return;
    }
    setCopySourceGroupId("");
    setCopyModalOpen(true);
  };

  const closeCopyModal = () => {
    if (copying) return;
    setCopyModalOpen(false);
    setCopySourceGroupId("");
  };

  const handleConfirmCopy = async () => {
    const sourceGroupId = String(copySourceGroupId ?? "").trim();
    const targetGroupId = String(selectedGroupId ?? "").trim();
    if (!targetGroupId) {
      toast.error("Select a schedule matrix group first");
      return;
    }
    if (!sourceGroupId) {
      toast.error("Select a group to copy from");
      return;
    }
    if (sourceGroupId === targetGroupId) {
      toast.error("Choose a different group to copy from");
      return;
    }
    if (!actionPermissions.canSave) return;

    setCopying(true);
    try {
      const response = await fetchAIScheduleMatrixByGroup(sourceGroupId);
      const sourceStages = extractStagesFromByGroupResponse(response);
      const sourceSections = stagesToSections(sourceStages);
      const nextSections = cloneSectionsForTargetGroup(sourceSections);

      const copyValidation = runGroupSectionsValidation(
        nextSections,
        isScheduleRowActive
      );
      if (!copyValidation.ok) {
        toast.error(
          copyValidation.message || "Source group has incomplete schedule rows"
        );
        return;
      }

      const rows = buildCopyRowsFromSourceStages(
        sourceStages,
        targetGroupId,
        buildFieldPayload
      );
      if (!rows.length) {
        toast.error("No schedule rows to copy from the selected group");
        return;
      }

      const saveResponse = await submitAIScheduleMatrixBulk(rows);
      if (!saveResponse || saveResponse?.success === false) {
        toast.error(saveResponse?.message || "Unable to copy schedule matrix");
        return;
      }

      setSections(nextSections);
      setMatrixLoaded(true);
      setOpenSection(nextSections.length > 0 ? 0 : null);
      setCopyModalOpen(false);
      setCopySourceGroupId("");
      toast.success(
        saveResponse?.message || "Schedule matrix copied successfully"
      );
      await loadMatrixForGroup(targetGroupId);
    } finally {
      setCopying(false);
    }
  };

  const toggleSection = (index) => {
    setOpenSection(openSection === index ? null : index);
  };

  const clearFieldErrorsForPatch = (sectionIndex, fieldIndex, patch) => {
    const keys = Object.keys(patch || {});
    if (!keys.length) return;
    setFieldErrors((prev) => {
      const sectionErr = prev[sectionIndex];
      if (!sectionErr?.[fieldIndex]) return prev;
      const nextFieldErr = { ...sectionErr[fieldIndex] };
      keys.forEach((key) => {
        delete nextFieldErr[key];
      });
      const nextSectionErr = { ...sectionErr };
      if (Object.keys(nextFieldErr).length) {
        nextSectionErr[fieldIndex] = nextFieldErr;
      } else {
        delete nextSectionErr[fieldIndex];
      }
      const next = { ...prev };
      if (Object.keys(nextSectionErr).length) {
        next[sectionIndex] = nextSectionErr;
      } else {
        delete next[sectionIndex];
      }
      return next;
    });
  };

  const updateField = (sectionIndex, fieldIndex, patch) => {
    clearFieldErrorsForPatch(sectionIndex, fieldIndex, patch);
    setSections((prev) =>
      prev.map((section, si) => {
        if (si !== sectionIndex) return section;
        return {
          ...section,
          fields: section.fields.map((field, fi) =>
            fi === fieldIndex ? { ...field, ...patch } : field
          ),
        };
      })
    );
  };

  const handleAddField = (sectionIndex) => {
    setSections((prev) =>
      prev.map((section, i) => {
        if (i !== sectionIndex) return section;
        const empty = createEmptyField();
        return {
          ...section,
          fields: reindexSectionFields([...section.fields, empty]),
        };
      })
    );
  };

  const handleScheduleDragStart = (start) => {
    if (start.type !== "SCHEDULE_ROW") return;
    const sectionIndex = String(start.source.droppableId).replace(
      "schedule-fields-",
      ""
    );
    const wrap = document.querySelector(
      `[data-schedule-matrix-table="${sectionIndex}"]`
    );
    if (wrap) {
      wrap.style.setProperty(
        "--schedule-matrix-drag-row-width",
        `${wrap.clientWidth}px`
      );
    }
  };

  const handleScheduleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.type !== "SCHEDULE_ROW") return;

    const sectionIndex = Number.parseInt(
      String(result.source.droppableId).replace("schedule-fields-", ""),
      10
    );
    if (!Number.isFinite(sectionIndex)) return;

    setSections((prev) =>
      prev.map((section, si) => {
        if (si !== sectionIndex) return section;
        const fields = [...section.fields];
        const [moved] = fields.splice(result.source.index, 1);
        fields.splice(result.destination.index, 0, moved);
        return { ...section, fields: reindexSectionFields(fields) };
      })
    );
  };

  const handleDeleteField = async (sectionIndex, fieldIndex) => {
    const field = sections[sectionIndex]?.fields?.[fieldIndex];
    if (field?.id) {
      const result = await Swal.fire({
        title: "Delete schedule row",
        text: "Remove this row? Save the group to apply changes on the server.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Remove",
        cancelButtonText: "Cancel",
      });
      if (!result.isConfirmed) return;
    }

    setSections((prev) =>
      prev.map((section, si) => {
        if (si !== sectionIndex) return section;
        const nextFields = section.fields.filter((_, fi) => fi !== fieldIndex);
        return {
          ...section,
          fields: reindexSectionFields(nextFields),
        };
      })
    );
  };

  const handleActionTypeChange = (sectionIndex, fieldIndex, selectedOption) => {
    const nextValue = selectedOption?.value ?? "";
    const keepSms = Number(nextValue) === AI_SCHEDULE_ACTION_TYPE.SMS;
    const field = sections[sectionIndex]?.fields?.[fieldIndex];
    updateField(sectionIndex, fieldIndex, {
      actionTypeId: nextValue,
      aiSmsTemplateId: keepSms ? field?.aiSmsTemplateId : "",
      aiSmsTemplateName: keepSms ? field?.aiSmsTemplateName : "",
    });
  };

  const handleSchedulePeriodChange = (sectionIndex, fieldIndex, selectedOption) => {
    const nextPeriod = selectedOption?.value ?? SCHEDULE_PERIOD_TYPE.DAY;
    updateField(sectionIndex, fieldIndex, {
      schedulePeriod: nextPeriod,
      dayNo: "",
      weekDayNo: "",
    });
  };

  const handleSmsTemplateChange = (sectionIndex, fieldIndex, selectedOption) => {
    updateField(sectionIndex, fieldIndex, {
      aiSmsTemplateId: selectedOption?.value ?? "",
      aiSmsTemplateName: selectedOption?.label ?? "",
    });
  };

  /** Same shape as v2 bulk add row; include `id` only when updating an existing row. */
  const buildFieldPayload = (
    groupId,
    section,
    field,
    fieldIndex,
    { asAdd = false } = {}
  ) => {
    const isDay = field.schedulePeriod === SCHEDULE_PERIOD_TYPE.DAY;
    const isSms = Number(field.actionTypeId) === AI_SCHEDULE_ACTION_TYPE.SMS;
    const payload = {
      rowIndex: toNumberOrZero(
        field.rowIndex === null || field.rowIndex === undefined || field.rowIndex === ""
          ? fieldIndex
          : field.rowIndex
      ),
      stageId: toNumberOrZero(section.stageId),
      scheduleMode: isDay ? SCHEDULE_MODE.DAY : SCHEDULE_MODE.WEEK,
      dayNo: isDay ? toOptionalNumber(field.dayNo) : null,
      weekDayNo: isDay ? null : toOptionalNumber(field.weekDayNo),
      time: normalizeTimeValue(field.time) || "00:00:00",
      groupId: String(groupId).trim(),
      actionTypeId: toNumberOrZero(field.actionTypeId),
      aiSmsTemplateId: isSms && field.aiSmsTemplateId
        ? String(field.aiSmsTemplateId)
        : null,
    };
    if (!asAdd && field.id) {
      payload.id = String(field.id);
    }
    return payload;
  };

  const buildRowsFromSections = (groupSections, groupId, { asAdd = false } = {}) =>
    (groupSections || []).flatMap((section) =>
      section.fields
        .map((field, fieldIndex) => ({ field, fieldIndex, section }))
        .filter(({ field }) => isScheduleRowActive(field))
        .map(({ field, fieldIndex, section }) =>
          buildFieldPayload(groupId, section, field, fieldIndex, { asAdd })
        )
    );

  const persistScheduleMatrix = async ({ onlySectionIndex } = {}) => {
    if (!selectedGroupId) {
      toast.error("Select a schedule matrix group first");
      return false;
    }
    const validation = runGroupSectionsValidation(sections, isScheduleRowActive, {
      onlySectionIndex,
    });
    if (!validation.ok) {
      setFieldErrors(validation.fieldErrors);
      if (validation.firstSectionIndex !== null) {
        setOpenSection(validation.firstSectionIndex);
      }
      toast.error(validation.message);
      return false;
    }
    setFieldErrors({});
    if (!actionPermissions.canSave) return false;

    const rows = buildRowsFromSections(sections, selectedGroupId, {
      asAdd: false,
    });

    setSaving(true);
    if (onlySectionIndex !== undefined && onlySectionIndex !== null) {
      setSavingSectionIndex(onlySectionIndex);
    } else {
      setSavingSectionIndex(null);
    }
    try {
      const response = await submitAIScheduleMatrixBulk(rows);
      if (!response || response?.success === false) {
        toast.error(response?.message || "Unable to save schedule matrix");
        return false;
      }
      toast.success(response?.message || "Schedule matrix updated successfully");
      setFieldErrors({});
      return true;
    } finally {
      setSaving(false);
      setSavingSectionIndex(null);
    }
  };

  const handleSaveAll = async () => {
    const saved = await persistScheduleMatrix();
    if (!saved) return;
    await loadMatrixForGroup(selectedGroupId);
  };

  const handleSaveSection = async (sectionIndex) => {
    const saved = await persistScheduleMatrix({ onlySectionIndex: sectionIndex });
    if (!saved) return;

    const nextOpenSection =
      sectionIndex + 1 < sections.length ? sectionIndex + 1 : sectionIndex;
    await loadMatrixForGroup(selectedGroupId, {
      openSectionIndex: nextOpenSection,
    });
  };

  const getSmsSelectOptions = (field) => {
    if (
      !field?.aiSmsTemplateId ||
      !field?.aiSmsTemplateName ||
      smsTemplateOptions.some(
        (opt) => String(opt.value) === String(field.aiSmsTemplateId)
      )
    ) {
      return smsTemplateOptions;
    }
    return [
      { value: field.aiSmsTemplateId, label: field.aiSmsTemplateName },
      ...smsTemplateOptions,
    ];
  };

  const sectionTitle = (section) => {
    const name = String(section?.stageName ?? "").trim();
    const stage = section?.stageId !== "" ? section.stageId : "—";
    return { name, stage };
  };

  const renderSelectPortalProps = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    styles: selectZIndexStyles,
  };

  const scheduleMatrixGroupSelectStyles = useMemo(
    () => ({
      ...selectZIndexStyles,
      menu: (base) => ({
        ...(typeof selectZIndexStyles.menu === "function"
          ? selectZIndexStyles.menu(base)
          : base),
        padding: 0,
        overflow: "hidden",
      }),
      option: (base) => ({
        ...base,
        display: "flex",
        alignItems: "center",
        width: "100%",
        padding: "0 0.5rem 0 0.75rem",
      }),
    }),
    []
  );

  const scheduleMatrixGroupSelectComponents = useMemo(() => {
    const { canEdit, canCreate } = groupActionPermissions;
    if (!canEdit && !canCreate) {
      return undefined;
    }

    const customComponents = {};

    if (canEdit) {
      function ScheduleMatrixGroupSelectOption(optionProps) {
        const groupId = optionProps?.data?.value;
        return (
          <components.Option {...optionProps}>
            <div className="schedule-matrix-group-option">
              <span className="schedule-matrix-group-option__label text-truncate">
                {optionProps.label}
              </span>
              <button
                type="button"
                className="schedule-matrix-group-option__edit"
                title="Edit group"
                aria-label={`Edit ${optionProps.label}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openGroupEditModal(groupId);
                }}
              >
                <i className="mdi mdi-pencil-outline" aria-hidden="true" />
              </button>
            </div>
          </components.Option>
        );
      }
      customComponents.Option = ScheduleMatrixGroupSelectOption;
    }

    if (canCreate) {
      function ScheduleMatrixGroupSelectMenuList(menuListProps) {
        return (
          <div className="schedule-matrix-group-menu">
            <components.MenuList {...menuListProps}>
              {menuListProps.children}
            </components.MenuList>
            <div
              className="schedule-matrix-group-menu-footer"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <button
                type="button"
                className="schedule-matrix-group-menu-footer__add"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openGroupCreateModal();
                }}
              >
                <i className="fa fa-plus" aria-hidden="true" />
                <span>Add group</span>
              </button>
            </div>
          </div>
        );
      }
      customComponents.MenuList = ScheduleMatrixGroupSelectMenuList;
    }

    return customComponents;
  }, [
    groupActionPermissions.canEdit,
    groupActionPermissions.canCreate,
    openGroupEditModal,
    openGroupCreateModal,
  ]);

  const stopRowDragForControl = (event) => {
    event.stopPropagation();
  };

  const renderStageSections = (groupSections) => {
    const list = Array.isArray(groupSections) ? groupSections : [];

    if (!list.length) {
      return (
        <p className="text-muted small mb-0 py-2">
          No schedule stages for this group.
        </p>
      );
    }

    return list.map((section, sectionIndex) => (
      <div
        key={section.localKey ?? sectionIndex}
        className="field-section mt-2 blue-border"
      >
        <div
          className="field-section-header d-flex justify-content-between align-items-center"
          onClick={() => toggleSection(sectionIndex)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              toggleSection(sectionIndex);
            }
          }}
        >
          <div className="d-flex align-items-center flex-wrap gap-2 section-badges">
            <span className="fw-semibold text-dark">{sectionTitle(section).name ||
                `Stage ${sectionTitle(section).stage}`}</span>
            {/* <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary">
              {sectionTitle(section).name ||
                `Stage ${sectionTitle(section).stage}`}
            </span> */}
          </div>
          <i
            className={`fa ${
              openSection === sectionIndex ? "fa-chevron-up" : "fa-chevron-right"
            } text-primary fs-6`}
            aria-hidden="true"
          />
        </div>
        <Collapse isOpen={openSection === sectionIndex}>
          <div className="field-section-body p-3 mt-0">
            {/* <div className="register-field-box schedule-matrix-fields register-field-box-radius"> */}
              {/* <div className="fw-bold mb-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
                <span>
                  <i
                    className="fa fa-calendar-check-o text-primary me-2"
                    aria-hidden="true"
                  />
                  Schedule entries
                </span>
                <span className="small fw-normal text-muted">
                  {section.fields.length} row
                  {section.fields.length === 1 ? "" : "s"}
                </span>
              </div> */}

              {!section.fields.length ? (
                <p className="schedule-matrix-no-data mb-0">
                  No schedules data found. Click{" "}
                  <span className="fw-semibold text-body-secondary">
                    Add schedule row
                  </span>{" "}
                  below to create one.
                </p>
              ) : null}

              {section.fields.length > 0 ? (
                <div
                  className="schedule-matrix-table-wrap"
                  data-schedule-matrix-table={sectionIndex}
                >
                <table className="table schedule-matrix-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="schedule-matrix-th-drag"
                        aria-label="Reorder"
                      />
                      <th scope="col" className="schedule-matrix-th-basis">
                        Basis
                        <RequiredMark />
                      </th>
                      <th scope="col" className="schedule-matrix-th-day-week">
                        Day / Week
                        <RequiredMark />
                      </th>
                      <th scope="col" className="schedule-matrix-th-time">
                        Time
                        <RequiredMark />
                      </th>
                      <th scope="col">
                        SMS template
                        <RequiredMark />
                      </th>
                      <th scope="col" className="schedule-matrix-th-actions" aria-label="Actions" />
                    </tr>
                  </thead>
                  <Droppable
                    droppableId={`schedule-fields-${sectionIndex}`}
                    type="SCHEDULE_ROW"
                  >
                    {(dropProvided) => (
                      <tbody
                        ref={dropProvided.innerRef}
                        {...dropProvided.droppableProps}
                      >
              {section.fields.map((field, fieldIndex) => {
                const actionTypeValue =
                  ACTION_TYPE_OPTIONS.find(
                    (opt) =>
                      String(opt.value) === String(field.actionTypeId ?? "")
                  ) || null;
                const periodValue =
                  SCHEDULE_PERIOD_OPTIONS.find(
                    (opt) => opt.value === field.schedulePeriod
                  ) || SCHEDULE_PERIOD_OPTIONS[0];
                const dayValue =
                  dayNoOptions.find(
                    (opt) => String(opt.value) === String(field.dayNo ?? "")
                  ) || null;
                const weekValue =
                  weekDayNoOptions.find(
                    (opt) =>
                      String(opt.value) === String(field.weekDayNo ?? "")
                  ) || null;
                const isSms =
                  Number(field.actionTypeId) === AI_SCHEDULE_ACTION_TYPE.SMS;
                const smsOptions = getSmsSelectOptions(field);
                const smsValue =
                  smsOptions.find(
                    (opt) =>
                      String(opt.value) === String(field.aiSmsTemplateId ?? "")
                  ) ||
                  (field.aiSmsTemplateId && field.aiSmsTemplateName
                    ? {
                        value: field.aiSmsTemplateId,
                        label: field.aiSmsTemplateName,
                      }
                    : null);
                const rowKey =
                  field.localKey ?? field.id ?? `${sectionIndex}-${fieldIndex}`;
                const draggableId = `schedule-field-${sectionIndex}-${rowKey}`;
                const cellErrors = fieldErrors[sectionIndex]?.[fieldIndex] || {};
                const rowHasErrors = Object.keys(cellErrors).length > 0;
                const invalidSelectClass = (key) =>
                  cellErrors[key] ? " is-invalid" : "";
                const invalidCellClass = (key) =>
                  cellErrors[key] ? " schedule-matrix-cell--invalid" : "";

                return (
                  <Draggable
                    key={draggableId}
                    draggableId={draggableId}
                    index={fieldIndex}
                  >
                    {(dragProvided, snapshot) => (
                  <tr
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    className={[
                      snapshot.isDragging ? "schedule-matrix-row--dragging" : "",
                      rowHasErrors ? "schedule-matrix-row--has-errors" : "",
                    ]
                      .filter(Boolean)
                      .join(" ") || undefined}
                  >
                    <td className="schedule-matrix-td-drag" data-label="Reorder">
                      <span
                        {...dragProvided.dragHandleProps}
                        className="schedule-matrix-drag-handle"
                        aria-label="Drag to reorder"
                      >
                        <i className="fa fa-grip-vertical" aria-hidden="true" />
                      </span>
                    </td>
                    <td data-label="Basis" className="schedule-matrix-td-basis">
                      <div
                        className={`schedule-matrix-cell${invalidCellClass("actionTypeId")}`}
                        onMouseDown={stopRowDragForControl}
                      >
                        <Select
                          options={ACTION_TYPE_OPTIONS}
                          className={`select2-selection schedule-matrix-select${invalidSelectClass("actionTypeId")}`}
                          placeholder="Call or SMS"
                          value={actionTypeValue}
                          onChange={(opt) =>
                            handleActionTypeChange(sectionIndex, fieldIndex, opt)
                          }
                          {...renderSelectPortalProps}
                        />
                      </div>
                    </td>
                    <td data-label="Day / Week" className="schedule-matrix-td-day-week">
                      <div className="schedule-matrix-day-week-cell">
                        <div
                          className="schedule-matrix-cell schedule-matrix-cell--period"
                          onMouseDown={stopRowDragForControl}
                        >
                          <Select
                            options={SCHEDULE_PERIOD_OPTIONS}
                            className="select2-selection schedule-matrix-select"
                            placeholder="Day or week"
                            value={periodValue}
                            onChange={(opt) =>
                              handleSchedulePeriodChange(sectionIndex, fieldIndex, opt)
                            }
                            {...renderSelectPortalProps}
                          />
                        </div>
                        <div
                          className={`schedule-matrix-cell schedule-matrix-cell--when${
                            invalidCellClass("dayNo") ||
                            invalidCellClass("weekDayNo")
                          }`}
                          onMouseDown={stopRowDragForControl}
                        >
                          {field.schedulePeriod === SCHEDULE_PERIOD_TYPE.DAY ? (
                            <Select
                              options={dayNoOptions}
                              className={`select2-selection schedule-matrix-select${invalidSelectClass("dayNo")}`}
                              placeholder="Day"
                              value={dayValue}
                              onChange={(opt) =>
                                updateField(sectionIndex, fieldIndex, {
                                  dayNo: opt?.value ?? "",
                                })
                              }
                              {...renderSelectPortalProps}
                            />
                          ) : (
                            <Select
                              options={weekDayNoOptions}
                              className={`select2-selection schedule-matrix-select${invalidSelectClass("weekDayNo")}`}
                              placeholder="Weekday"
                              value={weekValue}
                              onChange={(opt) =>
                                updateField(sectionIndex, fieldIndex, {
                                  weekDayNo: opt?.value ?? "",
                                })
                              }
                              {...renderSelectPortalProps}
                            />
                          )}
                        </div>
                      </div>
                    </td>
                    <td data-label="Time" className="schedule-matrix-td-time">
                      <div
                        className={`schedule-matrix-cell schedule-matrix-cell--time${invalidCellClass("time")}`}
                        onMouseDown={stopRowDragForControl}
                      >
                        <ScheduleMatrixTimeInput
                          className="form-control"
                          value={field.time || ""}
                          invalid={Boolean(cellErrors.time)}
                          onChange={(next) =>
                            updateField(sectionIndex, fieldIndex, {
                              time: clampScheduleTimeValue(next) || next,
                            })
                          }
                        />
                      </div>
                    </td>
                    <td data-label="SMS template">
                      <div
                        className={`schedule-matrix-cell${
                          !isSms ? " schedule-matrix-cell--disabled" : ""
                        }${invalidCellClass("aiSmsTemplateId")}`}
                        onMouseDown={stopRowDragForControl}
                      >
                        <Select
                          options={smsOptions}
                          className={`select2-selection schedule-matrix-select${invalidSelectClass("aiSmsTemplateId")}`}
                          placeholder={
                            !isSms
                              ? "N/A for Call"
                              : loadingTemplates
                                ? "Loading..."
                                : "Choose SMS template"
                          }
                          isLoading={loadingTemplates && isSms}
                          isDisabled={!isSms}
                          value={isSms ? smsValue : null}
                          onChange={(opt) =>
                            handleSmsTemplateChange(sectionIndex, fieldIndex, opt)
                          }
                          {...renderSelectPortalProps}
                        />
                      </div>
                    </td>
                    <td className="schedule-matrix-td-actions" data-label="Remove">
                      <button
                        type="button"
                        className="btn-row-delete"
                        aria-label="Remove row"
                        onClick={() => handleDeleteField(sectionIndex, fieldIndex)}
                      >
                        <i className="fa fa-trash" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                    )}
                  </Draggable>
                );
              })}
                        {dropProvided.placeholder}
                      </tbody>
                    )}
                  </Droppable>
                </table>
                </div>
              ) : null}

              <div className="schedule-matrix-section-footer mt-3">
                <Button
                  color="primary"
                  type="button"
                  outline
                  className="schedule-matrix-add-field schedule-matrix-section-btn"
                  onClick={() => handleAddField(sectionIndex)}
                  disabled={saving || loading || copying}
                >
                  <i className="fa fa-plus me-1" aria-hidden="true" />
                  Add schedule row
                </Button>
                {showToolbarActions && section.fields.length > 0 ? (
                  <Button
                    color="primary"
                    type="button"
                    className="schedule-matrix-section-save schedule-matrix-section-btn"
                    disabled={
                      saving || loading || copying || !matrixLoaded
                    }
                    onClick={() => handleSaveSection(sectionIndex)}
                  >
                    {saving && savingSectionIndex === sectionIndex
                      ? "Saving..."
                      : "Save"}
                  </Button>
                ) : null}
              </div>
            {/* </div> */}
          </div>
        </Collapse>
      </div>
    ));
  };

  return (
    <React.Fragment>
      <div className="page-content ai-schedule-matrix-page">
        <Container fluid>
          <Card className="shadow-sm border-0">
            <CardBody
              className={
                showMatrixEditor && sections.length ? "" : "card-body-90-vh"
              }
            >
              <h5 className="fw-bold mb-0 pe-2">AI Schedule Matrix</h5>
              <p className="text-muted small mb-0 mt-1">
                Choose a group, then configure call and SMS schedules for each stage.
              </p>
              <hr />

              <div
                className={`schedule-matrix-toolbar${
                  !showToolbarActions ? " schedule-matrix-toolbar--group-centered" : ""
                }`}
              >
                <Row
                  className={`align-items-end g-3${
                    !showToolbarActions ? " justify-content-center" : ""
                  }`}
                >
                  <Col xs={12} lg={showToolbarActions ? 9 : 6}>
                    <div className="schedule-matrix-group-field">
                      <div
                        className={`toolbar-label${
                          !showToolbarActions ? " text-center" : ""
                        }`}
                      >
                        Schedule Matrix Group
                      </div>
                      {optionsLoading ? (
                        <Spinners setLoading={setOptionsLoading} />
                      ) : scheduleMatrixGroupOptions.length ||
                        groupActionPermissions.canCreate ? (
                        <Select
                          options={scheduleMatrixGroupOptions}
                          className="select2-selection schedule-matrix-group-select"
                          classNamePrefix="schedule-matrix-group-rs"
                          placeholder="Select schedule matrix group"
                          value={getSelectedGroupOption()}
                          onChange={handleGroupChange}
                          isDisabled={loading}
                          components={scheduleMatrixGroupSelectComponents}
                          noOptionsMessage={() => "No groups found"}
                          styles={scheduleMatrixGroupSelectStyles}
                          menuPortalTarget={
                            typeof document !== "undefined" ? document.body : null
                          }
                          menuPosition="fixed"
                        />
                      ) : (
                        <p className="text-muted mb-0 small">
                          No schedule matrix groups found.
                        </p>
                      )}
                    </div>
                  </Col>
                  {showToolbarActions ? (
                    <>
                      <Col xs={6} lg={2}>
                        <Button
                          color="primary"
                          type="button"
                          className="schedule-matrix-save-btn w-100"
                          disabled={
                            saving || loading || copying || !matrixLoaded
                          }
                          onClick={handleSaveAll}
                        >
                          {saving ? "Saving..." : "Update"}
                        </Button>
                      </Col>
                      <Col xs={6} lg={1}>
                        <Button
                          color="success"
                          type="button"
                          className="schedule-matrix-copy-btn w-100"
                          disabled={loading || saving || copying}
                          onClick={openCopyModal}
                          title="Copy schedule from another group"
                        >
                          <i className="fa fa-copy me-1" aria-hidden="true" />
                          Copy
                        </Button>
                      </Col>
                    </>
                  ) : null}
                </Row>
              </div>

              <Modal
                isOpen={copyModalOpen}
                toggle={closeCopyModal}
                size="md"
                centered
                wrapClassName="schedule-matrix-copy-modal modal-full-height"
              >
                <ModalHeader toggle={closeCopyModal}>Copy schedule matrix</ModalHeader>
                <ModalBody>
                  <p className="text-muted small mb-2">
                    <span className="text-body-secondary">To group:</span>{" "}
                    <strong>
                      {getSelectedGroupOption()?.label ||
                        "Selected schedule matrix group"}
                    </strong>
                  </p>
                  <div
                    className="schedule-matrix-copy-note alert alert-danger mt-3 mb-0"
                    role="alert"
                    aria-label="Copy schedule note"
                  >
                    <div className="schedule-matrix-copy-note__header">
                      <i
                        className="mdi mdi-information-outline schedule-matrix-copy-note__icon"
                        aria-hidden="true"
                      />
                      <span className="schedule-matrix-copy-note__title">NOTE</span>
                    </div>
                    <p className="schedule-matrix-copy-note__text mb-0">
                      All schedule fields will be copied from{" "}
                      <strong>
                        {getCopySourceGroupOption()?.label || "From group"}
                      </strong>{" "}
                      to{" "}
                      <strong>
                        {getSelectedGroupOption()?.label || "To group"}
                      </strong>
                      . The current schedule configured for{" "}
                      <strong>
                        {getSelectedGroupOption()?.label || "To group"}
                      </strong>{" "}
                      will be fully replaced by the schedule from{" "}
                      <strong>
                        {getCopySourceGroupOption()?.label || "From group"}
                      </strong>
                      .
                    </p>
                  </div>
                  <Label className="fw-semibold mt-3">Copy From</Label>
                  <Select
                    options={copySourceGroupOptions}
                    className="select2-selection"
                    placeholder="Select from group"
                    value={getCopySourceGroupOption()}
                    onChange={(opt) =>
                      setCopySourceGroupId(opt?.value ? String(opt.value) : "")
                    }
                    isDisabled={copying}
                    {...renderSelectPortalProps}
                  />
                  
                </ModalBody>
                <ModalFooter>
                  <Button
                    color="secondary"
                    type="button"
                    outline
                    disabled={copying}
                    onClick={closeCopyModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    type="button"
                    disabled={copying || !copySourceGroupId}
                    onClick={handleConfirmCopy}
                  >
                    {copying ? "Copying..." : "Copy schedule"}
                  </Button>
                </ModalFooter>
              </Modal>

              {loading ? (
                <div className="mt-3">
                  <Spinners setLoading={setLoading} />
                </div>
              ) : null}

              {!loading && selectedGroupId && sections.length ? (
                <DragDropContext
                  onDragStart={handleScheduleDragStart}
                  onDragEnd={handleScheduleDragEnd}
                >
                  {renderStageSections(sections)}
                </DragDropContext>
              ) : null}

              {(!selectedGroupId ||
                (!loading && selectedGroupId && !sections.length)) && (
                <div className="schedule-matrix-empty">
                  <div className="empty-icon">
                    <i className="fa fa-table" aria-hidden="true" />
                  </div>
                  <h6 className="fw-semibold text-secondary mb-1">
                    {!selectedGroupId
                      ? "Select a schedule matrix group"
                      : "No schedules for this group"}
                  </h6>
                  <p className="small mb-0 mx-auto" style={{ maxWidth: "360px" }}>
                    {!selectedGroupId
                      ? "Pick a group above to load stage schedules."
                      : "There are no matrix rows yet for this group."}
                  </p>
                </div>
              )}

            </CardBody>
          </Card>

          <AIScheduleMatrixGroupAddEditModal
            isOpen={groupModalOpen}
            onClose={closeGroupModal}
            itemId={groupEditItemId}
            onSuccess={handleGroupModalSuccess}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

AIScheduleMatrix.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(AIScheduleMatrix);
